import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Textarea,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { COLORS } from "./styles/colors";
import { supabase } from "./utils/supabaseClient";

const CreditPayAPI = () => {
  const [loading, setLoading] = useState(false);
  const [showSkillsFutureForm, setShowSkillsFutureForm] = useState(false);
  const [claimRequest, setClaimRequest] = useState("");
  const [status, setStatus] = useState(null);
  const toast = useToast();

  // Form data with the specified values
  // const [formData, setFormData] = useState({
  //   courseId: "TGS-2025053785",
  //   courseFee: 1050,
  //   startDate: "2025-08-30",
  //   nric: "S9344000E",
  //   email: "brand0204@gmail.com",
  //   mobileNumber: "81576331",
  //   homeNumber: "81576331",
  //   additionalInformation: ""
  // });

  const [formData, setFormData] = useState({
    courseId: "TGS-2025055272",
    courseFee: 1200,
    startDate: "2025-08-26",
    nric: "S9344000E",
    email: "brand0204@gmail.com",
    mobileNumber: "81576331",
    homeNumber: "81576331",
    additionalInformation: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: 'info', message: 'Processing your request...' });

    try {
      const payload = {
        webhookData: formData,
        apiType: "Submit Claim",
        timestamp: new Date().toISOString(),
        source: 'sozcode-portal'
      };

      console.log('Sending request to relay-ssg:', payload);

      const { data, error } = await supabase.functions.invoke('relay-ssg', {
        body: payload,
        method: 'POST',
        mode: 'cors'
      });

      if (error) {
        throw new Error(`Relay failed: ${error.message}`);
      }

      console.log('Received response from relay-ssg:', data);

      if (data?.claim_request) {
        setClaimRequest(data.claim_request);
        setShowSkillsFutureForm(true);
        setStatus({ type: 'success', message: 'Request processed successfully! Please review and submit to continue to SkillsFuture.' });
        
        toast({
          title: "Success",
          description: "Your claim request has been processed. Please submit the form below to continue to SkillsFuture.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('No claim_request received from relay-ssg');
      }
    } catch (error) {
      console.error('Error:', error);
      
      setStatus({ type: 'error', message: `Error: ${error.message}` });
      
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkillsFutureSubmit = (e) => {
    e.preventDefault();
    
    // Create and submit the form to SkillsFuture
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://sfc.myskillsfuture.gov.sg/sfc2-ind/api/individual/sfcpayment/claim/submit/gateway';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'encryptedPayload';
    input.value = claimRequest;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'success':
        return 'green';
      case 'error':
        return 'red';
      case 'info':
        return 'blue';
      default:
        return 'gray';
    }
  };

  return (
    <Container 
      w={"100%"} 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      bg={COLORS.isabelline} 
      minH="100vh" 
      py={8}
      px={{ base: 4, md: 8 }}
    >
      <Box 
        width="100%" 
        bg="white" 
        maxW={{ base: "100%", md: "800px" }} 
        px={{ base: 4, md: 8 }} 
        py={{ base: 6, md: 8 }} 
        borderRadius="lg" 
        boxShadow="md"
      >
        <Heading 
          size={{ base: "md", md: "lg" }}
          mb={{ base: 6, md: 8 }} 
          color={COLORS.maastrichtBlue} 
          borderBottom="2px solid" 
          borderColor={COLORS.mandarin} 
          pb={2} 
          width="fit-content"
        >
          SkillsFuture Credit Pay Claim
        </Heading>

        {!showSkillsFutureForm ? (
          // Initial form
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color={COLORS.maastrichtBlue}>Course Information</Heading>
              
              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>Course ID</FormLabel>
                <Input
                  name="courseId"
                  value={formData.courseId}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>Course Fee</FormLabel>
                <Input
                  name="courseFee"
                  type="number"
                  value={formData.courseFee}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={COLORS.maastrichtBlue}>Course Run ID</FormLabel>
                <Input
                  name="courseRunId"
                  value={formData.courseRunId}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>Start Date</FormLabel>
                <Input
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <Heading size="md" color={COLORS.maastrichtBlue}>Individual Information</Heading>
              
              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>NRIC</FormLabel>
                <Input
                  name="nric"
                  value={formData.nric}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={COLORS.maastrichtBlue}>Mobile Number</FormLabel>
                <Input
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={COLORS.maastrichtBlue}>Home Number</FormLabel>
                <Input
                  name="homeNumber"
                  value={formData.homeNumber}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={COLORS.maastrichtBlue}>Additional Information</FormLabel>
                <Textarea
                  name="additionalInformation"
                  value={formData.additionalInformation}
                  onChange={handleInputChange}
                  borderColor={COLORS.maastrichtBlue}
                  rows={3}
                  bg="white"
                  color="black"
                />
              </FormControl>

              {status && (
                <Alert status={status.type} borderRadius="md">
                  <AlertIcon />
                  {status.message}
                </Alert>
              )}

              <Button
                type="submit"
                bg={COLORS.mandarin}
                color="white"
                size="lg"
                isLoading={loading}
                loadingText="Processing..."
                _hover={{ bg: '#e06835' }}
              >
                Submit Claim Request
              </Button>
            </VStack>
          </form>
        ) : (
          // SkillsFuture form
          <Box>
            <Alert status="info" mb={6} borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Ready to Submit to SkillsFuture</Text>
                <Text fontSize="sm">
                  Your claim request has been processed successfully. When you click submit below, 
                  you will be redirected to the SkillsFuture website to make payment under your SkillsFuture credits.
                </Text>
              </Box>
            </Alert>

            <form onSubmit={handleSkillsFutureSubmit}>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                    Instructions:
                  </Text>
                  <Text color={COLORS.maastrichtBlue} mb={4}>
                    Click the submit button below to be redirected to the SkillsFuture website where you can 
                    complete your payment using your SkillsFuture credits. Your claim data has been securely 
                    prepared and will be automatically submitted to SkillsFuture.
                  </Text>
                </Box>

                {/* Hidden input with the claim request */}
                <Input
                  type="hidden"
                  name="encryptedPayload"
                  value={claimRequest}
                />

                <Button
                  type="submit"
                  bg={COLORS.mandarin}
                  color="white"
                  size="lg"
                  _hover={{ bg: '#e06835' }}
                >
                  Continue to SkillsFuture
                </Button>

                <Button
                  variant="outline"
                  color={COLORS.maastrichtBlue}
                  borderColor={COLORS.maastrichtBlue}
                  onClick={() => {
                    setShowSkillsFutureForm(false);
                    setClaimRequest("");
                    setStatus(null);
                  }}
                  _hover={{ bg: COLORS.maastrichtBlue, color: "white" }}
                >
                  Back to Form
                </Button>
              </VStack>
            </form>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default CreditPayAPI; 